import { motion, useScroll, useTransform } from 'motion/react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import SplitText from '#app/components/motion/split-text.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import {
	useChangeBackgroundColor
} from '#app/hooks/use-change-background-color.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { cn } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'

interface MaterialShowcaseProps {
	className?: string
}

const materials = [
	{
		name: 'Cashmere',
		backgroundImage: '/img/finishes/5_cashmere-1.webp',
		id: 'cashmere',
	},
	{
		name: 'Natural',
		backgroundImage: '/img/finishes/1_natural-1.webp',
		id: 'natural',
	},
	{
		name: 'Polished',
		backgroundImage: '/img/finishes/2_polished-1.webp',
		id: 'polished',
	},
	{
		name: 'Silky',
		backgroundImage: '/img/finishes/3_silky-1.webp',
		id: 'silky',
	},
	{
		name: 'Structured',
		backgroundImage: '/img/finishes/4_structured-1.webp',
		id: 'structured',
	},
	{
		name: 'Top Lapped',
		backgroundImage: '/img/finishes/6_top-lapped-1.webp',
		id: 'top-lapped',
	},
]

export default function MaterialShowcase({ className }: MaterialShowcaseProps) {
	const { t } = useTranslation('sapienstone')
	const container = useRef<HTMLDivElement>(null)
	const ref = useChangeBackgroundColor(extendedTheme.colors.background)

	const { scrollYProgress } = useScroll({
		target: container,
		offset: ['start start', 'end end'],
	})

	// Create transforms for each material's opacity
	const materialOpacities = materials.map((_, index) => {
		const start = index / materials.length
		const end = (index + 1) / materials.length
		const transitionZone = 0.03 // Small transition zone for text

		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useTransform(
			scrollYProgress,
			[
				Math.max(0, start - transitionZone),
				start,
				end,
				Math.min(1, end + transitionZone),
			],
			[0.5, 1, 1, 0.5],
		)
	})

	// Create transforms for each background image's opacity
	const backgroundOpacities = materials.map((_, index) => {
		const start = index / materials.length
		const end = (index + 1) / materials.length
		const transitionZone = 0.02 // Much smaller transition window (2% instead of 10%)

		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useTransform(
			scrollYProgress,
			[
				Math.max(0, start - transitionZone),
				start,
				end,
				Math.min(1, end + transitionZone),
			],
			[0, 1, 1, 0],
		)
	})

	return (
		<div
			ref={container}
			className={cn(
				'relative max-w-[100vw] lg:max-w-[calc(100vw-100px)]',
				className,
			)}
		>
			<div ref={ref} className={'sticky top-0 h-screen overflow-hidden'}>
				{/* Background image that shows when section is over and backgroundOpacities are all 0. */}
				<motion.div
					className={'absolute inset-0 z-0 bg-cover bg-center bg-no-repeat'}
					style={{
						backgroundImage: `url(${materials[materials.length - 1]!.backgroundImage})`,
					}}
				/>

				{/* Background images */}
				{materials.map((material, index) => (
					<motion.div
						key={material.name}
						className={'absolute inset-0 z-10 bg-cover bg-center bg-no-repeat'}
						style={{
							backgroundImage: `url(${material.backgroundImage})`,
							opacity: backgroundOpacities[index],
						}}
						transition={{
							duration: 0.4,
							ease: [0.25, 0.1, 0.25, 1],
						}}
					/>
				))}

				{/* Overlay for better text readability */}
				<div className={'absolute inset-0 z-20 bg-black/15'} />

				{/*	Content grid */}
				<div
					className={
						'relative z-30 grid h-full grid-cols-1 gap-8 lg:grid-cols-2'
					}
				>
					{/* Left column, fixed text */}
					<div
						className={
							'order-2 flex flex-col justify-center px-6 lg:order-1 lg:px-12'
						}
					>
						<Text
							as={'h2'}
							size={'pageTitle'}
							className={'uppercase text-white'}
							weight={'light'}
							type={'serif'}
						>
							<SplitText as={'span'}>{t('finishes.title')}</SplitText>
						</Text>
					</div>

					{/* Right Column - Material Names */}
					<div className="order-1 flex flex-col justify-center px-6 lg:order-2 lg:px-12">
						<div className="space-y-8">
							{materials.map((material, index) => (
								<motion.div
									onClick={() => {
										const a = window.document.createElement('a')
										a.href = `${routes.public.products.list.route({})}?finish=${material.id}`
										a.target = '_blank'
										document.body.appendChild(a).click()
									}}
									key={material.name}
									className="cursor-pointer text-right lg:text-left"
									style={{
										opacity: materialOpacities[index],
									}}
									transition={{
										duration: 0.6,
										ease: [0.25, 0.1, 0.25, 1],
									}}
								>
									<Text
										type={'serif'}
										weight={'light'}
										as={'h3'}
										size={'h2'}
										className={'text-white'}
									>
										{material.name}
									</Text>
								</motion.div>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Spacer to create scroll distance */}
			<div style={{ height: `${materials.length * 50}vh` }} />
		</div>
	)
}
